import { action, computed, observable, runInAction } from "mobx";
import { FieldType, ViewModelBase } from "@shoothill/core";
import { FormRequestDTO, ProgrammeUpdatesModel, UsersListDTO } from "./ProgrammeUpdatesModel";
import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { CleanlinessReportStatusDTO } from "./CleanlinessReport/CleanlinessReportModel";
import { BuildingControlSiteVisitDTO } from "./BuildingControlSiteVisit/BuildingControlSiteVisitModel";
//import { CustomerVisitsSiteInstructionsDTO } from "./CustomerVisitsSiteInstructions/CustomerVisitsSiteInstructionsModel";
import { DailyProgrammeProgressUpdateV2DTO } from "./DailyProgrammeProgressUpdateV2/DailyProgrammeProgressUpdateV2Model";
import { OtherProgrammeNotesDTO } from "./OtherProgrammeNotes/OtherProgrammeNotesModel";
import { WeatherReportDTO } from "./WeatherReport/WeatherReportModel";
import { ProgrammeUpdatesDocumentsViewModel } from "./Documents/ProgrammeUpdatesDocumentsViewModel";
import { BuildingControlSiteVisitPhotoFile, BuildingControlReportPhotosFile } from "./BuildingControlSiteVisit/BuildingControlSiteVisitModel";
import { CleanlinessReportPhotosPhotoFile } from "./CleanlinessReport/CleanlinessReportModel";
//import { CustomerVisitsSitePhotosFile } from "./CustomerVisitsSiteInstructions/CustomerVisitsSiteInstructionsModel";
import { DailyProgrammeProgressUpdateFile } from "./DailyProgrammeProgressUpdate/DailyProgrammeProgressUpdateModel";
import { OtherProgrammeNotesFile } from "./OtherProgrammeNotes/OtherProgrammeNotesModel";
import { PotentialProgrammeDelaysPhotosFile } from "./PotentialProgrammeDelays/PotentialProgrammeDelaysModel";
import { OutstandingDesignInformationPhotosFile } from "./OutstandingDesignInformation/OutstandingDesignInformationModel";
import { MaterialStoragePhotosFile } from "./MaterialStorage/MaterialStorageModel";
import { LabourShortagePhotosFile } from "./LabourShortage/LabourShortageModel";
import { NonConformanceListDTO } from "./NonConformance/NonConformanceListModel";
import { TestAndInspectionsListDTO } from "./TestAndInspections/TestAndInspectionsListModel";
import { ProjectVisitorsListDTO } from "./ProjectVisitors/ProjectVisitorsListModel";
import { DailyProgrammeProgressUpdateV2ItemDTO } from "./DailyProgrammeProgressUpdateV2/DailyProgrammeProgressUpdateV2ItemModel";

export class ProgrammeUpdatesViewModel extends ViewModelBase<ProgrammeUpdatesModel> {
    public projectId: string = "";
    public date: Date = new Date();

    @observable
    public programmeUpdatesDocumentsViewModel: ProgrammeUpdatesDocumentsViewModel | null = null;

    @observable
    public cleanlinessReport: boolean = false;
    @observable
    public canDisplayWeatherReport: boolean = false;
    @observable
    public customerVisitsSiteInstructions: boolean = false;
    @observable
    public dailyProgrammeProgress: boolean = false;
    @observable
    public otherProgrammeNotes: boolean = false;
    @observable
    public potentialProgrammeDelays: boolean = false;
    @observable
    public outstandingDesignInformation: boolean = false;
    @observable
    public materialStorage: boolean = false;
    @observable
    public labourShortage: boolean = false;
    @observable
    public nonConformance: boolean = false;
    @observable
    public testAndInspections: boolean = false;
    @observable
    public cleanlinessReportFormId: string | null = null;
    @observable
    public potentialProgrammeDelaysId: string | null = null;
    @observable
    public outstandingDesignInformationId: string | null = null;
    @observable
    public materialStorageId: string | null = null;
    @observable
    public labourShortageId: string | null = null;
    @observable
    public dailyProgrammeProgressUpdateV2Id: string | null = null;

    @observable public buildingControlSiteVisitAttachments: (BuildingControlSiteVisitPhotoFile | BuildingControlReportPhotosFile)[] = [];
    @observable public cleanlinessReportAttachments: CleanlinessReportPhotosPhotoFile[] = [];
    @observable public customerVisitsList: ProjectVisitorsListDTO[] = [];
    @observable public dailyProgrammeProgressUpdateV2Items: DailyProgrammeProgressUpdateV2ItemDTO[] = [];
    @observable public otherProgrammeNotesAttachments: OtherProgrammeNotesFile[] = [];
    @observable public potentialProgrammeDelaysAttachments: PotentialProgrammeDelaysPhotosFile[] = [];
    @observable public outstandingDesignInformationAttachments: OutstandingDesignInformationPhotosFile[] = [];
    @observable public materialStorageAttachments: MaterialStoragePhotosFile[] = [];
    @observable public labourShortageAttachments: LabourShortagePhotosFile[] = [];
    @observable public nonConformanceItems: NonConformanceListDTO[] = [];
    @observable public testAndInspectionItems: TestAndInspectionsListDTO[] = [];

    constructor(projectId: string, date: Date, model = new ProgrammeUpdatesModel()) {
        super(model);
        this.setDecorators(ProgrammeUpdatesModel);
        this.projectId = projectId;
        this.date = date;
    }

    public server: ServerViewModel = new ServerViewModel();

    @observable
    public cleanlinessReportStatus = observable<CleanlinessReportStatusDTO>([]);
    @observable
    public usersList = observable<UsersListDTO>([]);
    @observable
    public buildingControlSiteVisit: BuildingControlSiteVisitDTO | null = null;
    @observable
    public weatherReport: WeatherReportDTO | null = null;
    // @observable
    // public customerVisitsSiteInstruction: CustomerVisitsSiteInstructionsDTO | null = null;
    @observable
    public dailyProgrammeProgressUpdate: DailyProgrammeProgressUpdateV2DTO | null = null;
    @observable
    public otherProgrammeNote: OtherProgrammeNotesDTO | null = null;

    @computed
    public get visitors() {
        return this.customerVisitsList.filter((v) => !v.isWorker);
    }

    @computed
    public get visitorsMessage() {
        const count = this.visitors.length;
        return `${count} visitor${count === 1 ? "" : "s"}`;
    }

    @computed
    public get workers() {
        return this.customerVisitsList.filter((v) => v.isWorker);
    }

    @computed
    public get workersMessage() {
        const count = this.workers.length;
        return `${count} worker${count === 1 ? "" : "s"}`;
    }

    @action
    public handleCleanlinessReportShow = () => {
        this.cleanlinessReport = true;
        this.cleanlinessReportFormId = null;
    };

    @action
    public cleanlinessReportId = (formId: string | null) => {
        this.cleanlinessReportFormId = formId;
        this.cleanlinessReport = true;
    };

    @action
    public potentialProgrammeDelayId = (formId: string | null) => {
        this.potentialProgrammeDelaysId = formId;
        this.potentialProgrammeDelays = true;
    };

    @action
    public outstandingDesignInformationIds = (formId: string | null) => {
        this.outstandingDesignInformationId = formId;
        this.outstandingDesignInformation = true;
    };

    @action
    public materialStorageIds = (formId: string | null) => {
        this.materialStorageId = formId;
        this.materialStorage = true;
    };

    @action
    public labourShortageIds = (formId: string | null) => {
        this.labourShortageId = formId;
        this.labourShortage = true;
    };

    @action
    public dailyProgrammeProgressUpdateV2Ids = (formId: string | null) => {
        this.dailyProgrammeProgressUpdateV2Id = formId;
        this.dailyProgrammeProgress = true;
    };

    @action
    public handleCleanlinessReportClose = () => {
        this.cleanlinessReport = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleWeatherReportShow = () => {
        this.canDisplayWeatherReport = true;
    };

    @action
    public handleWeatherReportClose = (reloadPage: boolean = false) => {
        this.canDisplayWeatherReport = false;

        if (reloadPage) {
            this.getProgrammeProgressUpdates();
        }
    };

    @action
    public handleCustomerVisitsSiteInstructionsShow = () => {
        this.customerVisitsSiteInstructions = true;
    };

    @action
    public handleCustomerVisitsSiteInstructionsClose = () => {
        this.customerVisitsSiteInstructions = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleDailyProgrammeProgressUpdateShow = () => {
        this.dailyProgrammeProgress = true;
        this.dailyProgrammeProgressUpdateV2Id = null;
    };

    @action
    public handleDailyProgrammeProgressUpdateClose = () => {
        this.dailyProgrammeProgress = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleOtherProgrammeNotesShow = () => {
        this.otherProgrammeNotes = true;
    };

    @action
    public handleOtherProgrammeNotesClose = () => {
        this.otherProgrammeNotes = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handlePotentialProgrammeDelaysShow = () => {
        this.potentialProgrammeDelays = true;
        this.potentialProgrammeDelaysId = null;
    };

    @action
    public handlePotentialProgrammeDelaysClose = () => {
        this.potentialProgrammeDelays = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleOutstandingDesignInformationShow = () => {
        this.outstandingDesignInformation = true;
        this.outstandingDesignInformationId = null;
    };

    @action
    public handleOutstandingDesignInformationClose = () => {
        this.outstandingDesignInformation = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleMaterialStorageShow = () => {
        this.materialStorage = true;
        this.materialStorageId = null;
    };

    @action
    public handleMaterialStorageClose = () => {
        this.materialStorage = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleLabourShortageShow = () => {
        this.labourShortage = true;
        this.labourShortageId = null;
    };

    @action
    public handleLabourShortageClose = () => {
        this.labourShortage = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleNonConformanceShow = () => {
        this.nonConformance = true;
    };

    @action
    public handleNonConformanceClose = () => {
        this.nonConformance = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public handleTestAndInspectionsShow = () => {
        this.testAndInspections = true;
    };

    @action
    public handleTestAndInspectionsClose = () => {
        this.testAndInspections = false;
        this.getProgrammeProgressUpdates();
    };

    @action
    public navigateBuildingControlSiteVisit = () => {
        this.history.push(AppUrls.Client.Project.ProgrammeUpdates.BuildingControlSiteVisit.Add.replace(":projectid", this.projectId).replace(":date", this.date.toISOString()));
    };

    @action
    public setBackGroundStatus(id: string | null): string {
        const cleanlinessReportStatus = this.cleanlinessReportStatus.find((i: any) => i.id === id);
        const color = cleanlinessReportStatus?.color;
        return color ? color : "";
    }

    @action
    public getUserName(id: string | null): string {
        const userList = this.usersList.find((i: any) => i.id === id);
        const getUserName = userList?.displayName;
        return getUserName ? getUserName : "";
    }

    // start getProgrammeProgressUpdates

    @action
    public getProgrammeProgressUpdates = async (): Promise<void> => {
        this.setIsLoading(true);
        const request: FormRequestDTO = {
            date: this.date.toISOString(),
            projectId: this.projectId,
            formId: null,
        };

        let apiResult = await this.Post<any>(AppUrls.Server.Projects.Construction.ProgrammeUpdates.GetProgrammeUpdatesAndRelated, request);
        if (apiResult) {
            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.model.fromDto(apiResult.payload);

                    // The users list is used for audit information.
                    this.usersList.replace(apiResult.payload.users);

                    this.cleanlinessReportStatus.replace(apiResult.payload.cleanlinessReportList?.cleanlinessReportStatus!);
                    this.buildingControlSiteVisit = apiResult.payload.buildingControlSiteVisitList?.buildingControlSiteVisit!;
                    this.weatherReport = apiResult.payload.weatherReportList?.weatherReport ?? null;
                    this.customerVisitsList = apiResult.payload.customerVisitsList?.siteAccessItems!;
                    this.dailyProgrammeProgressUpdate = apiResult.payload.dailyProgrammeProgressUpdateList?.dailyProgrammeProgressUpdate!;
                    this.otherProgrammeNote = apiResult.payload.otherProgrammeNoteList?.otherProgrammeNotes!;

                    this.programmeUpdatesDocumentsViewModel = new ProgrammeUpdatesDocumentsViewModel(this.projectId, this.date);

                    if (apiResult.payload.programmeUpdateDocuments) {
                        this.programmeUpdatesDocumentsViewModel.fromDto(apiResult.payload.programmeUpdateDocuments);
                    }
                    // Set document types if present in API response
                    if (apiResult.payload.programmeUpdateDocumentTypes) {
                        this.programmeUpdatesDocumentsViewModel.setProgrammeUpdateDocumentTypes(apiResult.payload.programmeUpdateDocumentTypes);
                    }

                    // Populate new attachment arrays
                    this.buildingControlSiteVisitAttachments = [
                        ...(apiResult.payload.buildingControlSiteVisitList?.buildingControlSiteVisitPhotos || []),
                        ...(apiResult.payload.buildingControlSiteVisitList?.buildingControlReportPhotos || []),
                    ];
                    this.cleanlinessReportAttachments = apiResult.payload.cleanlinessReportList?.cleanlinessReportPhotos || [];
                    //this.customerVisitsSiteInstructionsAttachments = apiResult.payload.customerVisitsSiteInstructionsList?.customerVisitsSiteInstructionsPhotos || [];
                    this.dailyProgrammeProgressUpdateV2Items =
                        apiResult.payload.dailyProgrammeProgressUpdateList?.dailyProgrammeProgressUpdateV2Items.filter((i: any) => i.id !== null) || [];
                    this.otherProgrammeNotesAttachments = apiResult.payload.otherProgrammeNoteList?.otherProgrammeNotesPhotos || [];
                    this.potentialProgrammeDelaysAttachments = apiResult.payload.potentialProgrammeDelayList?.potentialProgrammeDelaysPhotos || [];
                    this.outstandingDesignInformationAttachments = apiResult.payload.outstandingDesignInformationList?.outstandingDesignInformationPhotos || [];
                    this.materialStorageAttachments = apiResult.payload.materialStorageList?.materialStoragePhotos || [];
                    this.labourShortageAttachments = apiResult.payload.labourShortageList?.labourShortagePhotos || [];
                    this.nonConformanceItems = apiResult.payload.nonConformanceList?.nonConformanceListItems || [];
                    this.testAndInspectionItems = apiResult.payload.testAndInspectionsList?.inspections || [];
                });
            } else {
                console.log(apiResult.errors);
            }
        }
        this.setIsLoading(false);
    };

    // End getProgrammeProgressUpdates

    // start DownloadFile

    public DownloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
        try {
            const theUrl: URL = new URL(fileUrl);
            const cleanUrl: string = theUrl.origin + theUrl.pathname;

            const apiResult = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, cleanUrl, undefined, { responseType: "blob" });
            const response = apiResult as any;
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
        } catch (exception) {
            console.error(exception);
            this.setIsErrored(true);
        }
    };

    public OpenFile = (fileUrl: string) => {
        window.open(fileUrl, "_blank", "noopener,noreferrer");
    };

    // End DownloadFile

    public async isFieldValid(fieldName: keyof FieldType<ProgrammeUpdatesModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    get isFormDisabled() {
        return this.IsLoading || this.IsErrored;
    }
}
